import CartVariantPicker, { lineNeedsVariant, variantLabel } from '../../shared/CartVariantPicker';
import { canOnSale } from '../../permissions/access';
import React, { useState, useRef, useEffect } from 'react';
import { formatCurrency, PAYMENT_TYPES } from '../../checkout/utils/checkoutCalculations';
import { useSellerTaxStore, cartTaxBreakdown } from '../../taxes/sellerTax';
import { DISCOUNTS_VISIBLE } from '../../../config/featureFlags';
import toast from '../../shared/Toast';
import { useManualRule } from '../../discounts/manualRule';
import '../../discounts/till/till.css';

// Loyalty comps are removable pre-completion — no API call is made until the
// sale completes, so dropping the row is clean.
const REMOVABLE_TYPES = [PAYMENT_TYPES.CASH, PAYMENT_TYPES.CARD, PAYMENT_TYPES.OTHER, PAYMENT_TYPES.LOYALTY_REWARD];

// Feature-flagged off until the tip flow is reviewed end-to-end. Keep in
// sync with SummaryScreen.SHOW_TIP_OPTIONS.
const SHOW_TIP_OPTIONS = false;

const PAYMENT_TYPE_LABELS = {
  [PAYMENT_TYPES.CASH]: 'Cash',
  [PAYMENT_TYPES.CARD]: 'Card',
  [PAYMENT_TYPES.OTHER]: 'Other',
  [PAYMENT_TYPES.VOUCHER]: 'Voucher',
  [PAYMENT_TYPES.LOYALTY_REWARD]: 'Loyalty reward',
};

/**
 * CartPanel - POS cart using unified checkout design.
 * Two-row line items + More Options footer (matches diary SummaryScreen).
 */
const CartPanel = ({ state, actions, currencySymbol, onClose }) => {
  // Roles & permissions: discounting someone else's sale needs `discounts`.
  const canDiscount = canOnSale('discounts', (state.items || []).map((i) => i.staffId));
  // Selling below the menu: always logged for Reports; refused here only when
  // the owner chose 'refuse' (discounts/manualRule.js — the server checks too).
  const manualRule = useManualRule();
  // Tax lines follow the SELLER of the items in the cart ("Selling as"), not
  // the logged-in user — docs/tax-money-path.md §6. Informational in
  // inclusive mode; the balance already contains the tax.
  const sellerId = (state.items.find((i) => i.staffId) || {}).staffId || null;
  const sellerTaxStore = useSellerTaxStore(sellerId);
  const taxDiscountFactor = state.subtotal > 0 ? Math.max(0, (state.subtotal - (state.discountAmount || 0)) / state.subtotal) : 1;
  const taxLines = cartTaxBreakdown(sellerTaxStore, state.items, taxDiscountFactor);
  // Exclusive mode: the tax is charged on top, so it goes into the balance.
  const taxOnTop = taxLines && taxLines.mode === 'exclusive' ? taxLines.total : 0;
  useEffect(() => {
    if (actions.setTax && Math.abs((state.taxAmount || 0) - taxOnTop) >= 0.005) actions.setTax(taxOnTop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxOnTop]);
  const fmt = (amount) => formatCurrency(amount, currencySymbol);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  // Which line's pills are open, and the part-made choices per line. Keyed by
  // index and reset when a line resolves.
  const [editingVariantIndex, setEditingVariantIndex] = useState(null);
  const [variantSelections, setVariantSelections] = useState({});
  const menuRef = useRef(null);

  // A product with variants and none chosen cannot be sold: we would not know
  // what left the shelf. Pay is blocked until every line is resolved.
  const unresolvedVariantLines = (state.items || []).filter(lineNeedsVariant).length;

  const startEditing = (index, currentPrice) => {
    setEditingIndex(index);
    setEditPrice(currentPrice.toString());
  };

  const commitEdit = () => {
    if (editingIndex !== null) {
      const val = parseFloat(editPrice);
      // Typing a lower price takes money off, so it needs the Discount
      // permission (else it would get round every code and discount rule)
      const line = (state.items || [])[editingIndex];
      const listPrice = line ? (line.listPrice != null ? line.listPrice : line.price) : 0;
      if (manualRule === 'refuse' && !isNaN(val) && line && val < listPrice && !canOnSale('discounts', [line.staffId])) {
        toast.error('This salon only lets people with Discounts lower a price on someone else’s sale. Ask the salon owner if you need to.');
        setEditingIndex(null);
        setEditPrice('');
        return;
      }
      if (!isNaN(val) && val >= 0) {
        actions.updatePrice(editingIndex, val);
      }
      setEditingIndex(null);
      setEditPrice('');
    }
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') { setEditingIndex(null); setEditPrice(''); }
  };

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const isFullyPaid = state.balanceRemaining <= 0 && state.totalPaid > 0;

  if (state.screen !== 'summary') return null;

  return (
    <div className="co-layout">
      {/* Header */}
      <div className="co-header pos-cart-header">
        <div className="co-header-left">
          <h2 className="pos-cart-title">Cart</h2>
        </div>
        {onClose && (
          <button
            type="button"
            className="pos-cart-minimise"
            onClick={onClose}
            aria-label="Minimise cart"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 6l4 4 4-4" stroke="#1a1a1a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>

      {/* Scrollable body */}
      <div className="co-body">
        {/* Client */}
        {state.client ? (
          <div className="co-client-notes" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="co-avatar co-avatar--initials" style={{ width: 32, height: 32, fontSize: 13, flexShrink: 0 }}>
              {(state.client.name || 'W')[0].toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#1a1a1a' }}>{state.client.name}</div>
              {state.client.email && <div style={{ fontSize: 11, color: '#8e8e93', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{state.client.email}</div>}
            </div>
            <button className="co-remove-btn" onClick={() => actions.setClient(null)} style={{ width: 24, height: 24 }}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 2L8 8M8 2L2 8" stroke="#ef4444" strokeWidth="1.4" strokeLinecap="round"/></svg>
            </button>
          </div>
        ) : (
          <button className="pos-add-client-btn" onClick={() => actions.setScreen('clientPicker')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
            </svg>
            Add client
          </button>
        )}

        {/* Items */}
        {state.items.length === 0 ? (
          <div className="co-empty-state">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d1d6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 01-8 0" />
            </svg>
            <p className="co-empty">Cart is empty</p>
            <p style={{ fontSize: 12, color: '#aeaeb2', margin: '4px 0 0', textAlign: 'center' }}>Tap a product or service to add it</p>
          </div>
        ) : (
          <div className="co-section">
            <div className="co-section-title">Services & Items</div>
            {state.items.map((item, index) => (
              <div key={index} className={`co-line-item ${index === state.items.length - 1 ? 'pos-line-item--new' : ''}${lineNeedsVariant(item) ? ' co-line-item--needs-variant' : ''}`}>
                {/* Colour strip */}
                <div
                  className="co-line-item-color"
                  style={{ backgroundColor: item.type === 'service' ? (item.diaryColour || '#D6D5EC') : item.type === 'voucher' ? '#f59e0b' : '#29282D' }}
                />
                <div className="co-line-item-content">
                  {/* Row 1: name + remove */}
                  <div className="co-line-item-row1">
                    <div className="co-line-item-name">
                      {item.name}
                      {item.type === 'voucher' && <span className="co-line-item-badge co-line-item-badge--voucher">Voucher</span>}
                    </div>
                    <button className="co-remove-btn" onClick={() => actions.removeItem(index)}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M3 3L11 11M11 3L3 11" stroke="#ef4444" strokeWidth="1.4" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>
                  {/* Sub-info */}
                  {item.type === 'service' && item.staffName && (
                    <div className="co-line-item-sub">{item.staffName}</div>
                  )}
                  {/* A variant product resolves here — the line lands unpicked
                      and the pills are already open, so the next tap is the
                      one that matters. Once chosen it collapses to a subtitle
                      you can tap to change. */}
                  {item.type === 'product' && (item.variants || []).length > 0 && (
                    (!item.variantId || editingVariantIndex === index) ? (
                      <>
                        <CartVariantPicker
                          options={item.options}
                          variants={item.variants}
                          selection={variantSelections[index] || {}}
                          onChange={(sel) => setVariantSelections(prev => ({ ...prev, [index]: sel }))}
                          onResolved={(v) => {
                            actions.updateItem(index, {
                              variantId: v._id,
                              variantLabel: variantLabel(item.options, v),
                              price: (v.retailPrice !== null && v.retailPrice !== undefined) ? v.retailPrice : item.price,
                              stockPrice: (v.stockPrice !== null && v.stockPrice !== undefined) ? v.stockPrice : item.stockPrice,
                            });
                            setEditingVariantIndex(null);
                          }}
                        />
                        {!item.variantId && <p className="cvp-prompt">Choose {(item.options || []).map(o => o.name).join(' and ')} to sell this</p>}
                      </>
                    ) : (
                      <button type="button" className="cvp-chosen" onClick={() => setEditingVariantIndex(index)}>
                        {item.variantLabel} · change
                      </button>
                    )
                  )}
                  {/* Row 2: stepper + price */}
                  <div className="co-line-item-row2">
                    <div className="co-qty-stepper">
                      <button
                        className="co-qty-step"
                        onClick={() => {
                          if ((item.quantity || 1) <= 1) actions.removeItem(index);
                          else actions.setQuantity(index, (item.quantity || 1) - 1);
                        }}
                      >
                        {(item.quantity || 1) <= 1 ? (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                          </svg>
                        ) : (
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><line x1="2" y1="5" x2="8" y2="5"/></svg>
                        )}
                      </button>
                      <span className="co-qty-num">{item.quantity || 1}</span>
                      <button className="co-qty-step" onClick={() => actions.setQuantity(index, (item.quantity || 1) + 1)}>
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><line x1="5" y1="2" x2="5" y2="8"/><line x1="2" y1="5" x2="8" y2="5"/></svg>
                      </button>
                    </div>
                    {editingIndex === index ? (
                      <div className="co-inline-edit">
                        <span className="co-inline-edit-symbol">{currencySymbol}</span>
                        <input
                          className="co-inline-edit-input"
                          type="number"
                          step="0.01"
                          value={editPrice}
                          onChange={e => setEditPrice(e.target.value)}
                          onKeyDown={handleEditKeyDown}
                          onBlur={commitEdit}
                          autoFocus
                        />
                      </div>
                    ) : (
                      <>
                        {/* A code's saving on this line: the price it was, then what's paid */}
                        {item.promoOff > 0 && <span className="co-line-item-original">{fmt(item.price * (item.quantity || 1))}</span>}
                        <button className="co-price-btn" onClick={() => startEditing(index, item.price)}>
                          {fmt((item.price - (item.promoOff || 0)) * (item.quantity || 1))}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Totals */}
        {state.items.length > 0 && (
          <div className="co-totals">
            <div className="co-total-row">
              <span>Subtotal</span>
              <span>{fmt(state.subtotal)}</span>
            </div>

            {state.promo ? (
              <button className="co-total-row co-total-row--discount co-total-row--tappable dct-row-code" onClick={() => actions.setScreen('code')}>
                <span className="dct-row-label"><span className="dct-code dct-code--sm">{state.promo.code}</span>{state.promo.said && <span>{state.promo.said}</span>}</span>
                <span>-{fmt(state.discountAmount)}</span>
              </button>
            ) : state.discountAmount > 0 ? (
              <button className="co-total-row co-total-row--discount co-total-row--tappable" onClick={() => canDiscount && actions.setScreen('discount')}>
                <span>Discount{state.discount?.type === 'percentage' ? ` (${state.discount.value}%)` : ''}</span>
                <span>-{fmt(state.discountAmount)}</span>
              </button>
            ) : (
              <>
              {DISCOUNTS_VISIBLE && (
                <button className="co-total-row co-total-row--tappable" onClick={() => actions.setScreen('code')}>
                  <span style={{ color: '#007EA0' }}>Add a code</span>
                  <span style={{ color: '#8e8e93' }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 3L9 7L5 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                </button>
              )}
              <button className="co-total-row co-total-row--tappable" onClick={() => canDiscount && actions.setScreen('discount')}>
                <span style={{ color: '#007EA0' }}>Add discount</span>
                <span style={{ color: '#8e8e93' }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 3L9 7L5 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
              </button>
              </>
            )}
            {DISCOUNTS_VISIBLE && state.promoDropped && !state.promo && (
              <div className="dct-dropped" role="status">
                <span>{state.promoDropped} came off because the cart changed.</span>
                <button type="button" onClick={() => actions.setScreen('code')}>Add it again</button>
              </div>
            )}

            {taxLines && taxLines.rows.map((r) => (
              taxLines.mode === 'exclusive' ? (
                <div key={r.label} className="co-total-row">
                  <span>{r.label}</span>
                  <span>+{fmt(r.amount)}</span>
                </div>
              ) : (
                <div key={r.label} className="co-total-row" style={{ color: '#8e8e93', fontSize: 12.5 }}>
                  <span>Includes {r.label}</span>
                  <span>{fmt(r.amount)}</span>
                </div>
              )
            ))}
            {state.tipAmount > 0 && (
              <button className="co-total-row co-total-row--tip co-total-row--tappable" onClick={() => actions.setScreen('tip')}>
                <span>Tip{state.tip?.type ? ` (${state.tip.type})` : ''}</span>
                <span>{fmt(state.tipAmount)}</span>
              </button>
            )}

            {state.transactions.map((t, i) => (
              <div key={i} className="co-total-row co-total-row--paid">
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {t.label || PAYMENT_TYPE_LABELS[t.paymentType] || 'Payment'}
                  {REMOVABLE_TYPES.includes(t.paymentType) && (
                    <button className="co-remove-btn" style={{ width: 18, height: 18 }} onClick={() => actions.removeTransaction(i)}>
                      <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1.5 1.5L6.5 6.5M6.5 1.5L1.5 6.5" stroke="#ef4444" strokeWidth="1.2" strokeLinecap="round"/></svg>
                    </button>
                  )}
                </span>
                <span>-{fmt(t.amount)}</span>
              </div>
            ))}

            <div className="co-total-row co-total-row--balance">
              <span>Balance</span>
              <span className={state.balanceRemaining > 0 ? 'co-balance--due' : 'co-balance--paid'}>
                {state.balanceRemaining > 0 ? fmt(state.balanceRemaining) : 'Paid'}
              </span>
            </div>

            {/* Inline tip prompt when fully paid — skip if card reader already prompted.
                Currently feature-flagged off (see SHOW_TIP_OPTIONS at top of file). */}
            {SHOW_TIP_OPTIONS && isFullyPaid && state.tipAmount === 0
              && !state.transactions.some(t => t.paymentType === PAYMENT_TYPES.CARD_READER) && (
              <button className="co-tip-prompt" onClick={() => actions.setScreen('tip')}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 14.5S1.5 10.5 1.5 6.5C1.5 4.5 3 3 5 3c1.2 0 2.3.6 3 1.5C8.7 3.6 9.8 3 11 3c2 0 3.5 1.5 3.5 3.5 0 4-6.5 8-6.5 8z" stroke="#6366f1" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Add a tip?</span>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginLeft: 'auto' }}><path d="M5 3L9 7L5 11" stroke="#6366f1" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Footer: More Options + Pay */}
      {state.items.length > 0 && (
        <div className="bdp-footer">
          <div className="bdp-menu-wrap" ref={menuRef}>
            <button className="bdp-btn bdp-btn--secondary bdp-btn--menu" onClick={() => setMenuOpen(!menuOpen)}>
              <span>More Options</span>
              <svg className={`bdp-menu-chevron ${menuOpen ? 'bdp-menu-chevron--open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M3 7.5L6 4.5L9 7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {menuOpen && (
              <div className="bdp-menu">
                {SHOW_TIP_OPTIONS && (
                <button className="bdp-menu-item" onClick={() => { setMenuOpen(false); actions.setScreen('tip'); }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 14.5S1.5 10.5 1.5 6.5C1.5 4.5 3 3 5 3c1.2 0 2.3.6 3 1.5C8.7 3.6 9.8 3 11 3c2 0 3.5 1.5 3.5 3.5 0 4-6.5 8-6.5 8z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Add Tip
                </button>
                )}
                {DISCOUNTS_VISIBLE && <button className="bdp-menu-item" onClick={() => { setMenuOpen(false); actions.setScreen('code'); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z" /><circle cx="7.5" cy="7.5" r="1.2" fill="currentColor" />
                  </svg>
                  {state.promo ? <>Discount code<span className="dct-code dct-code--sm dct-menu-chip">{state.promo.code}</span></> : 'Add a code'}
                </button>}
                {canDiscount && <button className="bdp-menu-item" onClick={() => { setMenuOpen(false); actions.setScreen('discount'); }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12 6L8 10L4 6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.2"/>
                  </svg>
                  Add Discount
                </button>}
                <div className="bdp-menu-divider" />
                <button className="bdp-menu-item" onClick={() => { setMenuOpen(false); if (onClose) { onClose(); setTimeout(() => actions.clearCart(), 320); } else { actions.clearCart(); } }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <polyline points="3 5 4 5 13 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 5v8a1.5 1.5 0 01-1.5 1.5h-5A1.5 1.5 0 014 13V5m2 0V3.5A1.5 1.5 0 017.5 2h1A1.5 1.5 0 0110 3.5V5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Clear Cart
                </button>
                <button className="bdp-menu-item" onClick={() => { setMenuOpen(false); onClose(); }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                  </svg>
                  Close
                </button>
              </div>
            )}
          </div>
          <button
            className="bdp-btn bdp-btn--primary"
            onClick={() => {
              if (isFullyPaid) actions.markComplete();
              else actions.setScreen('payment');
            }}
            disabled={state.items.length === 0 || unresolvedVariantLines > 0}
            // Says WHICH thing is unfinished. A bare disabled button at the
            // till reads as broken.
            title={unresolvedVariantLines > 0 ? 'Choose an option on every product first' : undefined}
          >
            {unresolvedVariantLines > 0
              ? `Choose ${unresolvedVariantLines === 1 ? 'an option' : 'options'} to pay`
              : isFullyPaid ? 'Complete' : `Pay ${fmt(state.balanceRemaining)}`}
          </button>
        </div>
      )}
    </div>
  );
};

export default CartPanel;
